import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as ImageIcon, Upload, X, CheckCircle2, Send } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
// Drop-site photo upload — uses ImgBB's free anonymous upload API.
// To enable: sign up at https://imgbb.com/, grab an API key, and paste it
// below. With the key configured, the customer's photo is uploaded on
// submit and the resulting URL is included in the dispatch email so the
// driver can pull it up alongside the address.
const IMGBB_API_KEY = ''; // <-- paste your ImgBB API key here to enable photo delivery
export async function uploadDropSitePhoto(file: File): Promise<string | null> {
  if (!IMGBB_API_KEY) return null;
  try {
    const form = new FormData();
    form.append('image', file);
    form.append('name', `dropsite-${Date.now()}`);
    const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}&expiration=2592000`, {
      method: 'POST',
      body: form
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.data?.url || null;
  } catch (err) {
    console.error('Drop-site photo upload failed:', err);
    return null;
  }
}
export type DropSitePhotoValue = {
  file: File | null;
  preview: string | null;
};
type Props = {
  value: DropSitePhotoValue;
  onChange: (next: DropSitePhotoValue) => void;
  /** Max file size in megabytes. Defaults to 5. */
  maxMB?: number;
};
// Self-contained drop-site photo picker. Renders the file input, preview,
// and remove control. Controlled-input pattern: parent owns the
// `{file, preview}` value.
export function DropSitePhotoUpload({
  value,
  onChange,
  maxMB = 5
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) {
      alert(`File size must be less than ${maxMB}MB`);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      onChange({
        file,
        preview: reader.result as string
      });
    };
    reader.readAsDataURL(file);
  };
  const removePhoto = () => {
    onChange({
      file: null,
      preview: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  return <div data-id="element-737">
      <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-1" data-id="element-738">
        <ImageIcon className="w-5 h-5 mr-2 text-brand-muted" data-id="element-739" />
        Drop-Site Photo
        <span className="ml-1.5 text-xs font-medium text-stone-500 dark:text-stone-400" data-id="element-740">
          (optional)
        </span>
      </label>
      <p className="text-xs text-brand-muted dark:text-stone-400 mb-3" data-id="element-741">
        Snap a photo of the exact spot. We'll reference it when dispatching your
        driver.
      </p>
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" data-id="element-742" />
      {!value.preview ? <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full px-4 py-4 bg-white/60 dark:bg-white/10 backdrop-blur-sm border-2 border-dashed border-stone-300 dark:border-white/15 rounded-xl text-brand-charcoal dark:text-stone-200 hover:border-brand-orange/50 hover:bg-brand-orange/5 transition-all flex flex-col items-center gap-2" data-id="element-743">
          <Upload className="w-5 h-5 text-brand-muted" data-id="element-744" />
          <span className="text-sm font-semibold" data-id="element-745">
            Tap to take or upload a photo
          </span>
          <span className="text-xs text-brand-muted dark:text-stone-400" data-id="element-746">
            Max {maxMB}MB · JPG, PNG, HEIC
          </span>
        </button> : <div className="relative rounded-xl overflow-hidden border border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/5" data-id="element-747">
          <img src={value.preview} alt="Drop-site reference" className="w-full h-48 object-cover" data-id="element-748" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 flex items-center justify-between" data-id="element-749">
            <div className="min-w-0 flex-1" data-id="element-750">
              <p className="text-xs font-semibold text-white truncate" data-id="element-751">
                {value.file?.name}
              </p>
              <p className="text-[10px] text-white/70" data-id="element-752">
                Driver will see this reference
              </p>
            </div>
            <button type="button" onClick={removePhoto} className="ml-3 p-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors shrink-0" aria-label="Remove photo" data-id="element-753">
              <X className="w-4 h-4" data-id="element-754" />
            </button>
          </div>
        </div>}
    </div>;
}
// POST-ORDER variant: rendered on the cart drawer's success screen so the
// drop-site photo never blocks checkout. The customer can take their time
// (or skip it entirely). When they tap "Send to dispatch," the photo is
// uploaded to ImgBB and a short follow-up email is sent attaching the
// resulting URL to the order number so the driver can match it on dispatch.
//
// Owns its own state — parent doesn't need to plumb anything through.
type PostOrderProps = {
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  address: string;
};
type SendState = 'idle' | 'sending' | 'sent' | 'error';
export function PostOrderDropSitePhoto({
  orderNumber,
  customerName,
  customerEmail,
  address
}: PostOrderProps) {
  const [value, setValue] = useState<DropSitePhotoValue>({
    file: null,
    preview: null
  });
  const [sendState, setSendState] = useState<SendState>('idle');
  const handleSend = async () => {
    if (!value.file) return;
    setSendState('sending');
    try {
      const photoUrl = await uploadDropSitePhoto(value.file);
      // The dispatch email can only DELIVER the photo when we have a real
      // hosted URL for it (email can't carry the raw file). If the upload
      // is unavailable (no IMGBB_API_KEY configured) or fails, do NOT send
      // a useless filename-only email and do NOT pretend it succeeded —
      // that's how a photo silently never reaches dispatch. Surface an
      // error so the customer can use the text-it fallback instead.
      if (!photoUrl) {
        console.error('Drop-site photo not delivered: no hosted URL (configure IMGBB_API_KEY in DropSitePhotoUpload.tsx to enable photo delivery).');
        setSendState('error');
        return;
      }
      const body = `Drop-site photo for order ${orderNumber}.\n\nCustomer: ${customerName}\nAddress: ${address}\nPhoto URL: ${photoUrl}\n\nForward to the driver assigned to this delivery.`;
      const ok = await sendFormEmail({
        subject: `253Gravel — Drop-Site Photo for Order ${orderNumber}`,
        to_name: 'Dispatch',
        from_name: customerName,
        'Order Number': orderNumber,
        'Customer Name': customerName,
        'Delivery Address': address,
        'Drop-Site Photo': photoUrl,
        email: customerEmail,
        _replyto: customerEmail,
        message: body
      });
      setSendState(ok ? 'sent' : 'error');
    } catch (err) {
      console.error('Drop-site photo send failed:', err);
      setSendState('error');
    }
  };
  if (sendState === 'sent') {
    return <div className="rounded-2xl bg-green-50 dark:bg-green-500/[0.08] border border-green-200 dark:border-green-500/30 p-5 flex items-start gap-3" data-id="element-755">
        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" data-id="element-756" />
        <div className="min-w-0" data-id="element-757">
          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-758">
            Photo sent to dispatch
          </p>
          <p className="text-xs text-stone-600 dark:text-stone-300 mt-0.5" data-id="element-759">
            Your driver will reference it on delivery day.
          </p>
        </div>
      </div>;
  }
  return <div className="rounded-2xl bg-stone-50 dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 p-5" data-id="element-760">
      <DropSitePhotoUpload value={value} onChange={setValue} data-id="element-761" />
      <AnimatePresence data-id="element-762">
        {value.file && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-763">
            <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-764">
              <button type="button" onClick={handleSend} disabled={sendState === 'sending'} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-brand-button hover:bg-brand-buttonHover disabled:opacity-60 text-white font-bold text-sm transition-colors" data-id="element-765">
                {sendState === 'sending' ? <>Sending…</> : <>
                    <Send className="w-3.5 h-3.5" data-id="element-766" />
                    Send to dispatch
                  </>}
              </button>
              {sendState === 'error' && <span className="text-xs text-red-600 dark:text-red-400" data-id="element-767">
                  Couldn't send the photo. Please text it to dispatch at{' '}
                  <a href="sms:2066193742" className="font-bold underline" data-id="element-768">
                    (206) 619-3742
                  </a>{' '}
                  with your order number {orderNumber}.
                </span>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}